import type { ClassValue } from 'clsx'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { env } from '~/env'

/**
 * Combines and merges CSS classes using `clsx` and `tailwind-merge`.
 *
 * @param {...ClassValue} inputs CSS classes or class values to combine and merge.
 * @returns {string} A single string representing the combined and merged CSS classes.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Slices the string after a specified keyword.
 *
 * @param {string} value The string to be sliced.
 * @param {string} search The keyword after which to slice.
 * @returns {string} The substring from the end of the keyword to the end of the input string.
 */
export function sliceAfterKeyword(value: string, search: string) {
  const matchIndex = value.indexOf(search)

  if (matchIndex !== -1) {
    return value.substring(matchIndex + search.length)
  }

  return value
}

/**
 * Declines a noun depending on the number.
 *
 * @param {number} number - The number used to determine the noun form.
 * @param {string[]} titles - Array of noun forms for different cases.
 * @param {boolean} [full] - If true, returns the number along with the noun.
 * @return {string} - Returns the correct form of the noun based on the number.
 */
export function getNoun(number: number, titles: string[], full = false) {
  const cases = [2, 0, 1, 1, 1, 2]

  const result = titles[number % 100 > 4 && number % 100 < 20 ? 2 : cases[number % 10 < 5 ? number % 10 : 5]]
  return full ? `${number} ${result}` : result
}

// Tremor Raw focusInput [v0.0.1]
export const focusInput = [
  // base
  'focus:ring-2',
  // ring color
  'focus:ring-blue-200 focus:dark:ring-blue-700/30',
  // border color
  'focus:border-blue-500 focus:dark:border-blue-700',
]

// Tremor Raw focusRing [v0.0.1]
export const focusRing = [
  // base
  'outline outline-offset-2 outline-0 focus-visible:outline-2',
  // outline color
  'outline-blue-500 dark:outline-blue-500',
]

// Tremor Raw hasErrorInput [v0.0.1]
export const hasErrorInput = [
  // base
  'ring-2',
  // border color
  'border-red-500 dark:border-red-700',
  // ring color
  'ring-red-200 dark:ring-red-700/30',
]

/**
 * Adds CDN prefix to static asset paths if CDN URL is configured.
 * Works for files from public/ directory.
 *
 * @param {string} path - The asset path (e.g., '/assets/image.jpg')
 * @returns {string} The path with CDN prefix if configured, otherwise the original path
 */
export function getCdnPath(path: string): string {
  if (env.NEXT_PUBLIC_DISABLE_CDN === 'true' || env.NEXT_PUBLIC_NODE_ENV === 'development') {
    return path
  }

  if (env.NEXT_PUBLIC_CDN_URL && path.startsWith('/')) {
    return `${env.NEXT_PUBLIC_CDN_URL}/public${path}`
  }

  return path
}
